import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Admin: discounts — coupon CODES and automatic CONDITIONS in one list.
//
// The order of this list IS the rule: discounts are matched top to bottom
// (sort_order ASC) and the FIRST match wins — they never stack. The ↑/↓
// buttons move a row and post the whole ordered id list back, which the
// backend renumbers 10/20/30. See docs/DISCOUNTS.md.

export function AdminDiscountsPage() {
  const { t } = useTranslation()
  const { user, loading: authLoading } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (authLoading || !user || user.role !== 'admin') return
    api.admin.discounts.list()
      .then((d) => setRows(d.discounts || []))
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }, [authLoading, user])

  async function move(index, delta) {
    const next = [...rows]
    const to = index + delta
    if (to < 0 || to >= next.length) return
    ;[next[index], next[to]] = [next[to], next[index]]
    setRows(next)                       // optimistic — the order is the point
    setBusy(true); setError(null)
    try {
      const d = await api.admin.discounts.reorder(next.map((r) => r.id))
      setRows(d.discounts || next)
    } catch (e) {
      setError(e.code || 'server')
      setRows(rows)                     // put it back
    } finally { setBusy(false) }
  }

  async function remove(row) {
    if (!confirm(t('admin_discounts.confirm_delete', { name: row.name }))) return
    setBusy(true); setError(null)
    try {
      await api.admin.discounts.remove(row.id)
      const d = await api.admin.discounts.list()
      setRows(d.discounts || [])
    } catch (e) {
      setError(e.code || 'server')
    } finally { setBusy(false) }
  }

  function scope(r) {
    const parts = []
    if (r.course_name)      parts.push(r.course_name)
    if (r.participant_name) parts.push(r.participant_name)
    else if (r.user_name)   parts.push(r.user_name)
    if (r.applies_to !== 'all') parts.push(t(`admin_discounts.applies_${r.applies_to}`))
    return parts.length ? parts.join(' · ') : t('admin_discounts.scope_everyone')
  }

  function validity(r) {
    return r.valid_until
      ? t('admin_discounts.validity_range', { from: r.valid_from, until: r.valid_until })
      : t('admin_discounts.validity_open', { from: r.valid_from })
  }

  function uses(r) {
    return r.max_uses ? `${r.uses_count}/${r.max_uses}` : `${r.uses_count}/∞`
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user || user.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to="/admin">← {t('admin.title')}</Link></p>
      <h1>{t('admin_discounts.title')}</h1>
      <p className="muted">{t('admin_discounts.intro')}</p>
      <p className="muted small">{t('admin_discounts.order_note')}</p>
      <p>
        <Link to="/admin/discounts/new"><button type="button">{t('admin_discounts.add')}</button></Link>
        {' '}
        <Link to="/admin/discounts/log" style={{ marginLeft: '0.75rem' }}>
          {t('admin_discounts.log_link')}
        </Link>
      </p>

      {error && <p className="error">{t(`admin_discounts.errors.${error}`, {
        defaultValue: t(`admin.errors.${error}`, { defaultValue: error }),
      })}</p>}
      {loading && <p>{t('common.loading')}</p>}
      {!loading && rows.length === 0 && <p className="muted">{t('admin_discounts.empty_state')}</p>}

      {!loading && rows.length > 0 && (
        <table className="results-table">
          <thead>
            <tr>
              <th>{t('admin_discounts.col_order')}</th>
              <th>{t('admin_discounts.col_name')}</th>
              <th>{t('admin_discounts.col_value')}</th>
              <th>{t('admin_discounts.col_code')}</th>
              <th>{t('admin_discounts.col_scope')}</th>
              <th>{t('admin_discounts.col_validity')}</th>
              <th>{t('admin_discounts.col_uses')}</th>
              <th>{t('admin_discounts.col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button type="button" className="link-button" disabled={busy || i === 0}
                          title={t('admin_discounts.move_up')} onClick={() => move(i, -1)}>↑</button>
                  {' '}
                  <button type="button" className="link-button" disabled={busy || i === rows.length - 1}
                          title={t('admin_discounts.move_down')} onClick={() => move(i, 1)}>↓</button>
                </td>
                <td>
                  <Link to={`/admin/discounts/${r.id}/edit`}>{r.name}</Link>
                  {Number(r.active) !== 1 && (
                    <span className="badge muted-badge" style={{ marginLeft: '0.4rem' }}>
                      {t('admin_discounts.inactive')}
                    </span>
                  )}
                </td>
                <td>{r.type === 'percent' ? `${r.value}%` : r.value}</td>
                <td>{r.code || <span className="muted">{t('admin_discounts.automatic')}</span>}</td>
                <td>{scope(r)}</td>
                <td>{validity(r)}</td>
                <td>
                  <Link to={`/admin/discounts/log?discount_id=${r.id}`}>{uses(r)}</Link>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Link to={`/admin/discounts/${r.id}/edit`}>{t('common.edit')}</Link>
                  {' · '}
                  <button type="button" className="link-button" disabled={busy}
                          onClick={() => remove(r)}>{t('common.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  )
}
